import React from 'react';
import { INTERNAL_USERS_EXT, SA_ROLES_MOCK, SESSIONS_MOCK } from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import {
  ApiErrorState,
  EmptyState,
  MVP_INTERNAL_ROLES,
  SensitiveActionModal,
  SkeletonTable,
  USE_REAL_INTERNAL_USERS_API,
  authService,
  formatInternalUserError,
  internalUserService,
  mapFormToCreatePayload,
  readAuthState,
  useSuperAdminUsers,
} from './services.jsx';
import { ExportHistoryModal, SecuritySettingsModal } from './sistema-modals.jsx';
import { SuperAdminRoleBadge } from './tenant-status.jsx';
// Equipe e Permissões — Super Admin Tezus
// Gerenciamento de usuários internos, perfis, matriz de permissões, sessões e auditoria

const { useState: useStateEP, useEffect: useEffectEP, useMemo: useMemoEP, useCallback: useCallbackEP } = React;

// ── Permissões da plataforma ─────────────────────────────────────────────────
const ALL_PERMISSIONS = [
  // Tenants
  { code: 'tenants:read',        label: 'Ver tenants',            module: 'Tenants' },
  { code: 'tenants:create',      label: 'Criar tenant',           module: 'Tenants' },
  { code: 'tenants:update',      label: 'Atualizar tenant',       module: 'Tenants' },
  { code: 'tenants:suspend',     label: 'Suspender tenant',       module: 'Tenants' },
  { code: 'tenants:impersonate', label: 'Impersonar',             module: 'Tenants' },
  { code: 'tenants:repair',      label: 'Reparar tenant',         module: 'Tenants' },
  // Billing
  { code: 'billing:read',        label: 'Ver billing',            module: 'Billing' },
  { code: 'billing:update',      label: 'Atualizar billing',      module: 'Billing' },
  { code: 'billing:refund',      label: 'Reembolsos',             module: 'Billing' },
  // Usuários internos
  { code: 'users:read',          label: 'Ver usuários',           module: 'Usuários' },
  { code: 'users:create',        label: 'Criar usuário',          module: 'Usuários' },
  { code: 'users:update',        label: 'Atualizar usuário',      module: 'Usuários' },
  // Planos
  { code: 'plans:read',          label: 'Ver planos',             module: 'Planos' },
  { code: 'plans:update',        label: 'Atualizar planos',       module: 'Planos' },
  // Auditoria
  { code: 'audit:read',          label: 'Ver auditoria',          module: 'Auditoria' },
  { code: 'audit:export',        label: 'Exportar auditoria',     module: 'Auditoria' },
  // Sistema
  { code: 'observability:read',  label: 'Ver observabilidade',    module: 'Observabilidade' },
  { code: 'repairs:execute',     label: 'Executar repairs',       module: 'Repairs' },
  { code: 'integrations:read',   label: 'Ver integrações',        module: 'Integrações' },
  { code: 'integrations:update', label: 'Gerenciar integrações',  module: 'Integrações' },
  { code: 'tokens:read',         label: 'Ver tokens',             module: 'Tokens' },
  { code: 'tokens:revoke',       label: 'Revogar tokens',         module: 'Tokens' },
  { code: 'settings:update',     label: 'Alterar configurações',  module: 'Configurações' },
  // Suporte — Tickets
  { code: 'tickets:read',        label: 'Ver tickets',            module: 'Suporte' },
  { code: 'tickets:create',      label: 'Criar ticket',           module: 'Suporte' },
  { code: 'tickets:reply',       label: 'Responder ticket',       module: 'Suporte' },
  { code: 'tickets:close',       label: 'Encerrar ticket',        module: 'Suporte' },
  { code: 'tickets:escalate',    label: 'Escalar ticket',         module: 'Suporte' },
  { code: 'tickets:assign',      label: 'Atribuir ticket',        module: 'Suporte' },
  { code: 'tickets:delete',      label: 'Excluir ticket',         module: 'Suporte' },
  // Suporte — Base de Conhecimento
  { code: 'kb:read',             label: 'Ver artigos da KB',      module: 'Suporte' },
  { code: 'kb:create',           label: 'Criar / editar artigo',  module: 'Suporte' },
  { code: 'kb:publish',          label: 'Publicar artigo',        module: 'Suporte' },
  { code: 'kb:archive',          label: 'Arquivar artigo',        module: 'Suporte' },
  // Suporte — Macros e SLA
  { code: 'macros:use',          label: 'Usar macros',            module: 'Suporte' },
  { code: 'macros:manage',       label: 'Gerenciar macros',       module: 'Suporte' },
  { code: 'sla:read',            label: 'Ver regras de SLA',      module: 'Suporte' },
  { code: 'sla:manage',          label: 'Gerenciar regras de SLA',module: 'Suporte' },
  // Suporte — Relatórios
  { code: 'reports:read',        label: 'Ver relatórios',         module: 'Suporte' },
  { code: 'reports:export',      label: 'Exportar relatórios',    module: 'Suporte' },
];

const hasPermission = (role, permCode) => {
  if (!role || !role.permissions) return false;
  return role.permissions.includes('*') || role.permissions.includes(permCode);
};

// ── Drawer de novo usuário ────────────────────────────────────────────────────
const NewUserDrawer = ({ open, onClose, onSave, roles }) => {
  const [form, setForm] = useStateEP({ name: '', email: '', role_code: 'operations', password: '', require_mfa: true, send_invite: false, notes: '' });
  const [saving, setSaving] = useStateEP(false);
  const [saveError, setSaveError] = useStateEP('');

  useEffectEP(() => { if (!open) { setForm({ name: '', email: '', role_code: 'operations', password: '', require_mfa: true, send_invite: false, notes: '' }); setSaving(false); setSaveError(''); } }, [open]);

  if (!open) return null;
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try { await onSave(form); } catch (e) { setSaveError(formatInternalUserError(e)); } finally { setSaving(false); }
  };
  const canSave = form.name.trim() && form.email.includes('@') && form.role_code;

  const labelSt = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 };
  const inputSt = { width: '100%', height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.4)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 420, background: 'var(--bg-elevated)', height: '100%', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Novo usuário interno</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Acesso ao Super Admin da Tezus</div>
          </div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div><label style={labelSt}>Nome completo *</label><input style={inputSt} value={form.name} onChange={set('name')} placeholder="Ex: João Silva" /></div>
          <div><label style={labelSt}>E-mail *</label><input style={inputSt} type="email" value={form.email} onChange={set('email')} placeholder="joao@tezus.com" /></div>
          <div>
            <label style={labelSt}>Perfil de acesso *</label>
            <select value={form.role_code} onChange={set('role_code')} className="select" style={{ width: '100%' }}>
              {(roles || []).map(r => <option key={r.code} value={r.code}>{r.name} — {r.description}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.send_invite} onChange={set('send_invite')} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Enviar convite por e-mail</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>O usuário receberá um link para definir sua senha</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.require_mfa} onChange={set('require_mfa')} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Exigir MFA no primeiro login</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Recomendado para todos os perfis</div>
              </div>
            </label>
          </div>
          <div><label style={labelSt}>Senha inicial (opcional)</label><input style={inputSt} type="password" value={form.password} onChange={set('password')} placeholder="Deixe vazio para definir depois" autoComplete="new-password" /></div>
          <div><label style={labelSt}>Observações internas</label><textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Opcional..." style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} /></div>
          {saveError && <div style={{ padding: '10px 12px', background: 'var(--danger-bg)', borderRadius: 8, fontSize: 12, color: 'var(--danger)' }}>{saveError}</div>}
          <div style={{ padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            🔒 Esta ação será registrada na auditoria do Super Admin.
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'var(--bg-subtle)' }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Criando…</> : <><Icon name="plus" size={13} />Criar usuário</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Drawer edição simples (MVP Fase 7) ─────────────────────────────────────────
const EditUserDrawer = ({ open, user, roles, onClose, onSave }) => {
  const [form, setForm] = useStateEP({ name: '', email: '', role_code: 'admin', notes: '' });
  const [saving, setSaving] = useStateEP(false);
  const [saveError, setSaveError] = useStateEP('');

  useEffectEP(() => {
    if (open && user) {
      setForm({
        name: user.name,
        email: user.email,
        role_code: user.role_code || 'admin',
        notes: user.notes || '',
      });
      setSaveError('');
      setSaving(false);
    }
  }, [open, user]);

  if (!open || !user) return null;
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const labelSt = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 };
  const inputSt = { width: '100%', height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    const parts = form.name.trim().split(/\s+/).filter(Boolean);
    try {
      await onSave(user.id, {
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' '),
        role: form.role_code,
        notes: form.notes,
      });
    } catch (e) {
      setSaveError(formatInternalUserError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.4)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 420, background: 'var(--bg-elevated)', height: '100%', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 15 }}>Editar usuário</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user.email}</div></div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
          <div><label style={labelSt}>Nome completo</label><input style={inputSt} value={form.name} onChange={set('name')} /></div>
          <div><label style={labelSt}>E-mail</label><input style={{ ...inputSt, opacity: 0.7 }} value={form.email} disabled /></div>
          <div><label style={labelSt}>Perfil</label><select value={form.role_code} onChange={set('role_code')} className="select" style={{ width: '100%' }}>{(roles || []).map(r => <option key={r.code} value={r.code}>{r.name}</option>)}</select></div>
          <div><label style={labelSt}>Observações</label><textarea value={form.notes} onChange={set('notes')} rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }} /></div>
          {saveError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{saveError}</div>}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Usuários internos ─────────────────────────────────────────────────────
const TabUsuarios = ({ users, roles, onToast, onRefresh, listFilters, onListFiltersChange }) => {
  const [filterStatus, setFilterStatus] = useStateEP(listFilters?.status || 'all');
  const [filterRole, setFilterRole] = useStateEP(listFilters?.role || '');
  const [query, setQuery] = useStateEP(listFilters?.search || '');
  const [newDrawer, setNewDrawer] = useStateEP(false);
  const [editUser, setEditUser] = useStateEP(null);
  const [blockModal, setBlockModal] = useStateEP(null);
  const [unblockModal, setUnblockModal] = useStateEP(null);

  useEffectEP(() => {
    onListFiltersChange && onListFiltersChange({ search: query, role: filterRole, status: filterStatus });
  }, [query, filterRole, filterStatus]);

  const filtered = users;

  const counts = {
    all: users.length,
    active: users.filter(u => u.status === 'active').length,
    pending: users.filter(u => u.status === 'pending').length,
    inactive: users.filter(u => u.status === 'inactive').length,
    blocked: users.filter(u => u.status === 'blocked').length,
  };

  const currentUserId = (typeof readAuthState === 'function' ? readAuthState() : null)?.user?.id;

  const handleCreate = async (form) => {
    await internalUserService.createUser(mapFormToCreatePayload(form));
    setNewDrawer(false);
    onToast(`Usuário ${form.name} criado`);
    onRefresh();
  };

  const handleEditSave = async (userId, payload) => {
    await internalUserService.updateUser(userId, payload);
    setEditUser(null);
    onToast('Usuário atualizado');
    onRefresh();
  };

  const handleBlock = async ({ reason }) => {
    const user = blockModal;
    if (!user) return;
    try {
      await internalUserService.blockUser(user.id, reason);
      setBlockModal(null);
      onToast(`${user.name} bloqueado`);
      if (String(currentUserId) === String(user.id)) {
        await authService.logout();
        window.location.reload();
        return;
      }
      onRefresh();
    } catch (e) {
      onToast(formatInternalUserError(e));
      throw e;
    }
  };

  const handleUnblock = async ({ reason }) => {
    const user = unblockModal;
    if (!user) return;
    try {
      await internalUserService.unblockUser(user.id, reason);
      setUnblockModal(null);
      onToast(`${user.name} desbloqueado`);
      onRefresh();
    } catch (e) {
      onToast(formatInternalUserError(e));
      throw e;
    }
  };

  const MFABadge = ({ enabled }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: enabled ? '#e8f5ee' : '#fdecf0', color: enabled ? '#0f7a3e' : '#EC2A58', fontSize: 11, fontWeight: 600 }}>
      {enabled ? '✓ MFA' : '✗ MFA'}
    </span>
  );

  const StatusPill = ({ s }) => {
    const cfg = { active: { label: 'Ativo', bg: '#e8f5ee', color: '#0f7a3e' }, pending: { label: 'Pendente', bg: '#dde7ff', color: '#1d4ed8' }, inactive: { label: 'Inativo', bg: '#f4f5f7', color: '#6b7691' }, blocked: { label: 'Bloqueado', bg: '#fdecf0', color: '#EC2A58' } };
    const c = cfg[s] || cfg.inactive;
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 11, fontWeight: 600 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />{c.label}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: 320 }}>
          <Icon name="search" size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar por nome ou e-mail..." style={{ width: '100%', height: 34, paddingLeft: 34, paddingRight: 12, border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }} />
        </div>
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="select" style={{ height: 34, minWidth: 160 }}>
          <option value="">Todos os perfis</option>
          {(roles || []).map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ id: 'all', label: 'Todos', count: counts.all }, { id: 'active', label: 'Ativos', count: counts.active }, { id: 'pending', label: 'Pendentes', count: counts.pending }, { id: 'inactive', label: 'Inativos', count: counts.inactive }, { id: 'blocked', label: 'Bloqueados', count: counts.blocked || 0 }].map(f => (
            <button key={f.id} onClick={() => setFilterStatus(f.id)} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: filterStatus === f.id ? 'var(--brand-navy)' : 'var(--bg-subtle)', color: filterStatus === f.id ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              {f.label} <span style={{ opacity: 0.75 }}>({f.count})</span>
            </button>
          ))}
        </div>
        <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setNewDrawer(true)}>
          <Icon name="plus" size={13} />Novo usuário
        </button>
      </div>

      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Usuário', 'Perfil', 'Status', 'MFA', 'Último acesso', 'Criado em', 'Ações'].map(h => (
              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '11px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1B3D60', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                      {u.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '11px 14px' }}><SuperAdminRoleBadge role={{ code: u.role_code, name: u.role }} /></td>
                <td style={{ padding: '11px 14px' }}><StatusPill s={u.status} /></td>
                <td style={{ padding: '11px 14px' }}><MFABadge enabled={u.mfa_enabled} /></td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{u.last_seen}</td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{u.created_at}</td>
                <td style={{ padding: '11px 14px' }}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button className="icon-action" title="Editar" onClick={() => setEditUser(u)}><Icon name="edit" size={13} /></button>
                    {u.status === 'blocked' || u.status === 'inactive'
                      ? <button className="icon-action" title="Desbloquear" onClick={() => setUnblockModal(u)}><Icon name="play" size={13} /></button>
                      : <button className="icon-action danger" title="Bloquear" onClick={() => setBlockModal(u)}><Icon name="suspend" size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <EmptyState icon="users" title="Nenhum usuário encontrado" description="Ajuste os filtros ou crie um novo usuário." />}
      </div>

      <NewUserDrawer open={newDrawer} onClose={() => setNewDrawer(false)} roles={roles} onSave={handleCreate} />
      <EditUserDrawer open={!!editUser} user={editUser} roles={roles} onClose={() => setEditUser(null)} onSave={handleEditSave} />
      <SensitiveActionModal
        open={!!blockModal}
        onClose={() => setBlockModal(null)}
        onConfirm={handleBlock}
        title={`Bloquear ${blockModal?.name || 'usuário'}`}
        description="O operador perderá acesso ao Super Admin imediatamente."
        warning={String(currentUserId) === String(blockModal?.id) ? 'Você está bloqueando sua própria conta e será desconectado.' : null}
        confirmLabel="Bloquear usuário"
        danger
      />
      <SensitiveActionModal
        open={!!unblockModal}
        onClose={() => setUnblockModal(null)}
        onConfirm={handleUnblock}
        title={`Desbloquear ${unblockModal?.name || 'usuário'}`}
        description="O operador voltará a ter acesso ao Super Admin."
        confirmLabel="Desbloquear usuário"
      />
    </div>
  );
};

// ── Tab: Perfis de acesso ─────────────────────────────────────────────────────
const TabPerfis = ({ roles, onToast }) => (
  <div>
    <div style={{ background: 'var(--info-bg)', border: '1px solid color-mix(in srgb,var(--info) 30%,var(--border))', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
      <strong style={{ color: 'var(--text)' }}>Perfis MVP (metadados).</strong> Estes perfis organizam operadores internos; permissões granulares por código ficam fora desta fase.
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
      {roles.map(r => (
        <div key={r.id} className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <SuperAdminRoleBadge role={{ code: r.code, name: r.name }} />
                {r.is_system && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--brand-navy)', color: 'white', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sistema</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{r.description}</div>
            </div>
            {!r.is_system && <button className="icon-action" title="Editar" onClick={() => onToast('Editor de perfil em breve')}><Icon name="edit" size={13} /></button>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
            <strong>{r.users}</strong> usuário{r.users !== 1 ? 's' : ''} · <strong>{r.permissions[0] === '*' ? 'Todos' : r.permissions.length}</strong> permissões
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {r.permissions[0] === '*'
              ? <span style={{ fontSize: 11, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>*:* (Acesso total)</span>
              : r.permissions.slice(0, 4).map(p => (
                <span key={p} style={{ fontSize: 10, background: 'var(--bg-subtle)', color: 'var(--text-muted)', padding: '2px 7px', borderRadius: 20, fontFamily: 'JetBrains Mono, monospace' }}>{p}</span>
              ))}
            {r.permissions.length > 4 && <span style={{ fontSize: 10, color: 'var(--text-muted)', padding: '2px 7px' }}>+{r.permissions.length - 4}</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Tab: Matriz de permissões ─────────────────────────────────────────────────
const TabMatriz = ({ roles, onToast }) => {
  const modules = [...new Set(ALL_PERMISSIONS.map(p => p.module))];

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
        Matriz avançada permanece como protótipo visual — <strong>fora do MVP</strong>. A aba Usuários usa a API real; permissões por código serão evoluídas pós-MVP.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--bg-subtle)', position: 'sticky', left: 0, zIndex: 2, minWidth: 200, borderBottom: '1px solid var(--border)' }}>Permissão</th>
            {roles.map(r => (
              <th key={r.id} style={{ padding: '10px 14px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', minWidth: 100 }}>
                {r.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {modules.map(mod => {
            const perms = ALL_PERMISSIONS.filter(p => p.module === mod);
            return (
              <React.Fragment key={mod}>
                <tr>
                  <td colSpan={roles.length + 1} style={{ padding: '8px 14px', background: 'var(--bg-subtle)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '2px solid var(--border)' }}>
                    {mod}
                  </td>
                </tr>
                {perms.map(perm => (
                  <tr key={perm.code} style={{ borderTop: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '8px 14px', position: 'sticky', left: 0, background: 'var(--bg-elevated)', zIndex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 12 }}>{perm.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{perm.code}</div>
                    </td>
                    {roles.map(r => {
                      const has = hasPermission(r, perm.code);
                      return (
                        <td key={r.id} style={{ padding: '8px 14px', textAlign: 'center' }}>
                          <div title={has ? `${r.name} tem permissão ${perm.code}` : `${r.name} não tem permissão ${perm.code}`}
                            style={{ width: 20, height: 20, borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: has ? '#e8f5ee' : 'var(--bg-subtle)', cursor: 'default' }}>
                            {has
                              ? <Icon name="check" size={11} style={{ color: '#0f7a3e', strokeWidth: 3 }} />
                              : <span style={{ width: 6, height: 2, borderRadius: 1, background: 'var(--border-strong)', display: 'block' }} />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Tab: Sessões e segurança ──────────────────────────────────────────────────
const TabSessoes = ({ onToast }) => {
  const sessions = SESSIONS_MOCK || [];
  const StatusSessionBadge = ({ s, suspicious }) => {
    const cfg = { active: { label: 'Ativa', bg: '#e8f5ee', color: '#0f7a3e' }, expired: { label: 'Expirada', bg: '#f4f5f7', color: '#6b7691' }, suspicious: { label: 'Suspeita', bg: '#fdecf0', color: '#EC2A58' } };
    const c = cfg[suspicious ? 'suspicious' : s] || cfg.expired;
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 11, fontWeight: 600 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />{c.label}</span>;
  };

  const usersNoMFA = (INTERNAL_USERS_EXT || []).filter(u => !u.mfa_enabled && u.status === 'active');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Alertas de segurança */}
      {(sessions.some(s => s.suspicious) || usersNoMFA.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sessions.filter(s => s.suspicious).map(s => (
            <div key={s.id} style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="alert" size={18} style={{ color: 'var(--danger)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--danger)' }}>Sessão suspeita detectada — {s.user}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>IP: {s.ip} · {s.location} · {s.device}</div>
              </div>
              <button className="btn danger" style={{ fontSize: 12 }} onClick={() => onToast(`Sessão de ${s.user} revogada`)}>Revogar sessão</button>
            </div>
          ))}
          {usersNoMFA.length > 0 && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="shield" size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--warning)' }}>{usersNoMFA.length} usuário{usersNoMFA.length > 1 ? 's' : ''} sem MFA ativo</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{usersNoMFA.map(u => u.name).join(', ')}</div>
              </div>
              <button className="btn" style={{ fontSize: 12 }} onClick={() => onToast('MFA exigido para todos os usuários sem autenticação')}>Exigir MFA para todos</button>
            </div>
          )}
        </div>
      )}

      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Sessões recentes</div>
          <button className="btn sm ghost" onClick={() => onToast('Todas as sessões revogadas')}><Icon name="lock" size={12} />Revogar todas</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Usuário', 'IP · Dispositivo', 'Localização', 'Iniciada', 'Última atividade', 'Status', 'Ações'].map(h => (
              <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {sessions.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid var(--border)', background: s.suspicious ? 'color-mix(in srgb, var(--danger-bg) 30%, transparent)' : '' }}>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{s.user}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{s.email}</div>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{s.ip}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.device}</div>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{s.location}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{s.started}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{s.last_activity}</td>
                <td style={{ padding: '10px 14px' }}><StatusSessionBadge s={s.status} suspicious={s.suspicious} /></td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {s.status === 'active' && <button className="icon-action danger" title="Revogar sessão" onClick={() => onToast(`Sessão de ${s.user} revogada`)}><Icon name="x" size={13} /></button>}
                    {s.suspicious && <button className="icon-action danger" title="Bloquear usuário" onClick={() => onToast(`${s.user} bloqueado`)}><Icon name="suspend" size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Page principal ─────────────────────────────────────────────────────────────
const EquipePermissoesPage = ({ onToast, logAction }) => {
  const [activeTab,       setActiveTab]       = useStateEP('usuarios');
  const [listFilters, setListFilters] = useStateEP({ search: '', role: '', status: 'all' });
  const { data: users, loading, error, refetch } = useSuperAdminUsers(listFilters);
  const roles = useMemoEP(() => (USE_REAL_INTERNAL_USERS_API ? MVP_INTERNAL_ROLES : (SA_ROLES_MOCK || MVP_INTERNAL_ROLES)), []);

  // Fase 2 — modais
  const [exportModal,        setExportModal]        = useStateEP(false);
  const [securityModal,      setSecurityModal]       = useStateEP(false);
  const [newUserDirect,      setNewUserDirect]       = useStateEP(false);
  const [lastExport,         setLastExport]          = useStateEP(null);
  const [lastSecurityUpdate, setLastSecurityUpdate]  = useStateEP(null);

  const safeUsers = users || [];

  const TABS = [
    { id: 'usuarios',  label: 'Usuários internos', mvp: true },
    { id: 'perfis',    label: 'Perfis de acesso', prototype: true },
    { id: 'matriz',    label: 'Matriz de permissões', prototype: true },
    { id: 'suporte',   label: 'Permissões de Suporte', prototype: true },
    { id: 'sessoes',   label: 'Sessões e segurança', prototype: true },
    { id: 'historico', label: 'Histórico de acessos', prototype: true },
  ].filter(t => !USE_REAL_INTERNAL_USERS_API || !t.prototype);

  const handleEquipeTab = (tabId) => {
    const tab = TABS.find(t => t.id === tabId);
    if (tab?.prototype && USE_REAL_INTERNAL_USERS_API) {
      onToast && onToast(`${tab.label} — fora do MVP. Use Usuários internos.`);
      return;
    }
    setActiveTab(tabId);
  };

  const kpis = [
    { label: 'Usuários ativos',   value: safeUsers.filter(u => u.status === 'active').length,   color: 'var(--success)' },
    { label: 'Pendentes',         value: safeUsers.filter(u => u.status === 'pending').length,  color: 'var(--info)' },
    { label: 'Perfis de acesso',  value: roles.length,                                           color: 'var(--text)' },
    { label: 'Super admins',      value: safeUsers.filter(u => u.role_code === 'owner' || u.role_code === 'admin').length, color: 'var(--danger)' },
    { label: 'Sem MFA',           value: safeUsers.filter(u => !u.mfa_enabled && u.status === 'active').length, color: 'var(--warning)' },
  ];

  return (
    <div className="content" style={{ maxWidth: 1200 }}>
      <div className="page-header">
        <div>
          <h1>Equipe e Permissões</h1>
          <p className="sub">Controle de acesso interno ao Super Admin da Tezus.</p>
        </div>
        <div className="actions">
          {lastExport && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Icon name="clock" size={11} />Última exportação: {lastExport}
            </span>
          )}
          {lastSecurityUpdate && (
            <span style={{ fontSize: 11, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Icon name="check" size={11} />Segurança atualizada: {lastSecurityUpdate}
            </span>
          )}
          {!USE_REAL_INTERNAL_USERS_API && (
            <>
              <button className="btn" onClick={() => setExportModal(true)}><Icon name="download" size={13} />Exportar histórico</button>
              <button className="btn" onClick={() => setSecurityModal(true)}><Icon name="settings" size={13} />Segurança</button>
            </>
          )}
          <button className="btn primary" onClick={() => setNewUserDirect(true)}><Icon name="plus" size={13} />Novo usuário</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {kpis.map(k => (
          <div key={k.label} className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.color, letterSpacing: '-0.02em' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 20, gap: 2 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => handleEquipeTab(t.id)} style={{ padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400, color: activeTab === t.id ? 'var(--text)' : 'var(--text-muted)', borderBottom: activeTab === t.id ? '2px solid var(--brand-navy)' : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {t.label}
            {t.prototype && !USE_REAL_INTERNAL_USERS_API && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-subtle)', padding: '1px 5px', borderRadius: 4 }}>Protótipo</span>}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody><SkeletonTable rows={6} cols={7} /></tbody></table>
        </div>
      )}
      {error && <ApiErrorState error={error} onRetry={refetch} />}
      {!loading && !error && (
        <>
          {activeTab === 'usuarios'  && <TabUsuarios users={safeUsers} roles={roles} onToast={onToast} onRefresh={refetch} listFilters={listFilters} onListFiltersChange={setListFilters} />}
          {activeTab === 'perfis'    && <TabPerfis   roles={roles} onToast={onToast} />}
          {activeTab === 'matriz'    && <TabMatriz   roles={roles} onToast={onToast} />}
          {activeTab === 'suporte'   && <TabSuporte  onToast={onToast} />}
          {activeTab === 'sessoes'   && <TabSessoes  onToast={onToast} />}
          {activeTab === 'historico' && (
            <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Histórico de acessos</div>
                <button className="btn sm ghost" onClick={() => onToast('CSV exportado')}><Icon name="download" size={12} />Exportar</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--bg-subtle)' }}>
                  <tr>{['Data/hora', 'Usuário', 'IP', 'Dispositivo', 'Resultado', 'Ações'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {[
                    { date: 'Hoje, 08:30', user: 'Julio Lazzaron', ip: '177.142.12.8', device: 'Chrome 124 · macOS', result: 'success', reason: null },
                    { date: 'Hoje, 03:22', user: 'Fernando Ortiz',  ip: '45.162.90.12', device: 'Chrome 124 · macOS', result: 'suspicious', reason: 'IP geolocation anomaly' },
                    { date: 'Ontem, 17:00',user: 'Pedro Chaves',    ip: '191.205.44.22', device: 'Firefox 125', result: 'success', reason: null },
                    { date: 'Ontem, 12:15',user: 'Desconhecido',    ip: '201.88.44.11',  device: 'Bot/Unknown', result: 'failed', reason: 'Credencial inválida' },
                    { date: '21 Mai, 09:00',user: 'Beatriz Lima',   ip: '189.101.88.55', device: 'Safari 17 · iOS', result: 'success', reason: null },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{row.date}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 500, fontSize: 13 }}>{row.user}</td>
                      <td style={{ padding: '10px 14px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)' }}>{row.ip}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{row.device}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {row.result === 'success'    && <span style={{ fontSize: 12, color: '#0f7a3e', fontWeight: 600 }}>✓ Sucesso</span>}
                        {row.result === 'failed'     && <span style={{ fontSize: 12, color: '#EC2A58', fontWeight: 600 }}>✗ Falha — {row.reason}</span>}
                        {row.result === 'suspicious' && <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>⚠ Suspeita</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <button className="icon-action" title="Ver auditoria" onClick={() => onToast('Abrindo log de auditoria')}><Icon name="audit" size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Fase 2 — modais acionados pelo header */}
      <NewUserDrawer
        open={newUserDirect}
        onClose={() => setNewUserDirect(false)}
        roles={roles}
        onSave={async (data) => {
          try {
            await internalUserService.createUser(mapFormToCreatePayload(data));
            setNewUserDirect(false);
            onToast(`Usuário ${data.name} criado`);
            logAction && logAction({ acao: `Criou usuário interno ${data.email}`, kind: 'info' });
            refetch();
          } catch (e) {
            onToast(formatInternalUserError(e));
            throw e;
          }
        }}
      />
      <ExportHistoryModal
        open={exportModal}
        onClose={() => setExportModal(false)}
        onExport={({ format }) => {
          setExportModal(false);
          setLastExport('agora');
          onToast(`Histórico exportado como .${format.toUpperCase()}`);
          logAction && logAction({ acao: 'Exportou histórico de acessos da equipe', kind: 'info' });
        }}
      />
      <SecuritySettingsModal
        open={securityModal}
        onClose={() => setSecurityModal(false)}
        onSave={(cfg) => {
          setSecurityModal(false);
          if (cfg._revokeAll) {
            onToast('Todas as sessões ativas foram revogadas');
            logAction && logAction({ acao: 'Revogou todas as sessões ativas', kind: 'danger' });
          } else {
            setLastSecurityUpdate('agora');
            onToast('Configurações de segurança salvas');
            logAction && logAction({ acao: 'Atualizou configurações de segurança da equipe', kind: 'info' });
          }
        }}
      />
    </div>
  );
};

// ── Tab: Permissões de Suporte ─────────────────────────────────────────────────
const SUPPORT_PROFILES = [
  { id: 'support_l1',      label: 'Suporte N1',         color: '#1d4ed8', bg: '#dde7ff' },
  { id: 'support_l2',      label: 'Suporte N2',         color: '#7c3aed', bg: '#ede9fe' },
  { id: 'support_manager', label: 'Gerente Suporte',    color: '#0f7a3e', bg: '#e8f5ee' },
  { id: 'kb_editor',       label: 'Editor de Base',     color: '#b45309', bg: '#fef3c7' },
  { id: 'kb_publisher',    label: 'Publicador de Base', color: '#2e7d7d', bg: '#d1fafa' },
];

const SUPPORT_PERM_GROUPS = [
  { group: 'Tickets', perms: [
    { code: 'support:tickets:read',            label: 'Ver tickets'           },
    { code: 'support:tickets:create',          label: 'Criar tickets'         },
    { code: 'support:tickets:reply',           label: 'Responder tickets'     },
    { code: 'support:tickets:assign',          label: 'Atribuir tickets'      },
    { code: 'support:tickets:close',           label: 'Encerrar tickets'      },
    { code: 'support:tickets:internal_note',   label: 'Notas internas'        },
    { code: 'support:tickets:escalate',        label: 'Escalar tickets'       },
    { code: 'support:tickets:change_priority', label: 'Alterar prioridade'    },
    { code: 'support:tickets:change_status',   label: 'Alterar status'        },
  ]},
  { group: 'Macros, SLA & Relatórios', perms: [
    { code: 'support:macros:read',    label: 'Ver macros'           },
    { code: 'support:macros:manage',  label: 'Gerenciar macros'     },
    { code: 'support:sla:read',       label: 'Ver regras de SLA'    },
    { code: 'support:sla:manage',     label: 'Gerenciar SLA'        },
    { code: 'support:reports:read',   label: 'Ver relatórios'       },
  ]},
  { group: 'Base de Conhecimento', perms: [
    { code: 'kb:articles:read',        label: 'Ver artigos'          },
    { code: 'kb:articles:create',      label: 'Criar artigos'        },
    { code: 'kb:articles:update',      label: 'Editar artigos'       },
    { code: 'kb:articles:publish',     label: 'Publicar artigos'     },
    { code: 'kb:articles:archive',     label: 'Arquivar artigos'     },
    { code: 'kb:categories:manage',    label: 'Gerenciar categorias' },
    { code: 'kb:settings:manage',      label: 'Configurações KB'     },
  ]},
];

const SUPPORT_DEFAULT_PERMS = {
  support_l1:      ['support:tickets:read','support:tickets:create','support:tickets:reply','support:tickets:internal_note','support:macros:read','support:sla:read','kb:articles:read'],
  support_l2:      ['support:tickets:read','support:tickets:create','support:tickets:reply','support:tickets:assign','support:tickets:close','support:tickets:internal_note','support:tickets:escalate','support:tickets:change_priority','support:tickets:change_status','support:macros:read','support:macros:manage','support:sla:read','support:reports:read','kb:articles:read'],
  support_manager: ['support:tickets:read','support:tickets:create','support:tickets:reply','support:tickets:assign','support:tickets:close','support:tickets:internal_note','support:tickets:escalate','support:tickets:change_priority','support:tickets:change_status','support:macros:read','support:macros:manage','support:sla:read','support:sla:manage','support:reports:read','kb:articles:read','kb:categories:manage'],
  kb_editor:       ['support:tickets:read','kb:articles:read','kb:articles:create','kb:articles:update'],
  kb_publisher:    ['support:tickets:read','kb:articles:read','kb:articles:create','kb:articles:update','kb:articles:publish','kb:articles:archive','kb:categories:manage'],
};

const TabSuporte = ({ onToast }) => {
  const [perms, setPerms] = useStateEP(() => {
    const init = {};
    SUPPORT_PROFILES.forEach(p => { init[p.id] = new Set(SUPPORT_DEFAULT_PERMS[p.id] || []); });
    return init;
  });
  const [showDenied, setShowDenied] = useStateEP(false);
  const [saved, setSaved] = useStateEP(false);

  const toggle = (profileId, permCode) => {
    setPerms(prev => {
      const next = { ...prev };
      const s = new Set(prev[profileId]);
      s.has(permCode) ? s.delete(permCode) : s.add(permCode);
      next[profileId] = s;
      return next;
    });
    setSaved(false);
  };

  const handleSave = () => {
    setSaved(true);
    onToast('Permissões de suporte salvas');
  };

  const allCodes = SUPPORT_PERM_GROUPS.flatMap(g => g.perms.map(p => p.code));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Aviso */}
      <div style={{ background: 'var(--info-bg)', border: '1px solid color-mix(in srgb,var(--info) 30%,var(--border))', borderRadius: 10, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="shield" size={15} style={{ color: 'var(--info)', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text)' }}>Acesso limitado ao suporte.</strong> Perfis de suporte não têm acesso automático a billing sensível, configurações globais críticas ou ações destrutivas de tenant.
        </div>
      </div>

      {/* Exemplo: sem permissão */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={'btn sm' + (showDenied ? ' primary' : '')} onClick={() => setShowDenied(d => !d)}>
          <Icon name="alert" size={12} />Simular: Suporte N1 acessa Billing
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Veja o estado de acesso negado</span>
      </div>

      {showDenied && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid color-mix(in srgb,var(--danger) 30%,var(--border))', borderRadius: 12, padding: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="shield" size={20} style={{ color: 'var(--danger)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--danger)', marginBottom: 4 }}>Acesso negado</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
              O perfil <strong>Suporte Nível 1</strong> não tem permissão para acessar <strong>Billing & Inadimplência</strong>. Esta área requer os perfis <code style={{ background: 'var(--bg-subtle)', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>billing:read</code> ou superior.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--bg-elevated)', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: 12, fontWeight: 600 }}>
                <Icon name="x" size={11} />billing:read — Negado
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--bg-elevated)', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: 12, fontWeight: 600 }}>
                <Icon name="x" size={11} />billing:update — Negado
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Matriz */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-elevated)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 200, position: 'sticky', left: 0, background: 'var(--bg-subtle)', zIndex: 2 }}>
                Permissão
              </th>
              {SUPPORT_PROFILES.map(p => (
                <th key={p.id} style={{ padding: '10px 14px', textAlign: 'center', minWidth: 120 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, background: p.bg, color: p.color, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {p.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SUPPORT_PERM_GROUPS.map(group => (
              <React.Fragment key={group.group}>
                <tr>
                  <td colSpan={SUPPORT_PROFILES.length + 1} style={{ padding: '8px 14px', background: 'color-mix(in srgb,var(--brand-navy) 5%,var(--bg-subtle))', fontSize: 11, fontWeight: 700, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                    {group.group}
                  </td>
                </tr>
                {group.perms.map((perm, pi) => (
                  <tr key={perm.code} style={{ borderBottom: '1px solid var(--border)', background: pi % 2 === 0 ? 'var(--bg-elevated)' : 'color-mix(in srgb,var(--brand-navy) 1%,var(--bg-elevated))' }}>
                    <td style={{ padding: '10px 14px', position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{perm.label}</div>
                      <code style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{perm.code}</code>
                    </td>
                    {SUPPORT_PROFILES.map(profile => {
                      const has = perms[profile.id]?.has(perm.code);
                      return (
                        <td key={profile.id} style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <button
                            onClick={() => toggle(profile.id, perm.code)}
                            title={has ? 'Clique para revogar' : 'Clique para conceder'}
                            style={{
                              width: 28, height: 28, borderRadius: 6,
                              border: `2px solid ${has ? profile.color : 'var(--border-strong)'}`,
                              background: has ? profile.bg : 'transparent',
                              color: has ? profile.color : 'var(--text-muted)',
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 120ms',
                            }}
                          >
                            {has ? <Icon name="check" size={13} /> : <Icon name="x" size={11} />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
        {saved && <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 500 }}>✓ Permissões salvas</span>}
        <button className="btn ghost" onClick={() => { setPerms(() => { const init = {}; SUPPORT_PROFILES.forEach(p => { init[p.id] = new Set(SUPPORT_DEFAULT_PERMS[p.id] || []); }); return init; }); setSaved(false); }}>
          Restaurar padrão
        </button>
        <button className="btn primary" onClick={handleSave}>
          <Icon name="check" size={13} />Salvar permissões
        </button>
      </div>
    </div>
  );
};

export {
  EquipePermissoesPage,
};
